import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { getBuyer, BUYERS, BUYER_COLORS, fmtDate, sortBuyerNames, useDepartmentMap, resolveAllowedBuyers } from "../utils/buyerMapping";
import { styleLookup } from "../services/api";
import { ui, Button, Badge, Segmented, Spinner, ErrorState } from "./common/ui";
import StyleProfitLookup from "./StyleProfitLookup"; // 2026-10-20 style profit lookup
import FlowStrip from "./FlowStrip"; // 2026-10-12 per-session reorder flow strip (FLOW_CONTRACT.md)

// Neutral fallback when a buyer has no colour assigned (tokens, so it works in dark mode).
const NO_COLOR = { bg: "var(--bg-hover)", text: "var(--text-muted)" };

const SORT_INIT = { col: "request_date", dir: "desc" };

// Given a session's dept_breakdown dict and a buyer (or null for all),
// return aggregated { total, created, pending, pct, avg_days }
function aggregateForBuyer(session, buyer) {
  const depts = session.dept_breakdown || {};
  let total = 0, created = 0, daysSum = 0, daysCount = 0;
  Object.entries(depts).forEach(([dept, v]) => {
    if (buyer && getBuyer(dept) !== buyer) return;
    total += v.total;
    created += v.created;
    if (v.avg_days !== null) { daysSum += v.avg_days * v.created; daysCount += v.created; }
  });
  return {
    total,
    created,
    pending: total - created,
    pct: total ? +(created / total * 100).toFixed(1) : 0,
    avg_days: daysCount ? +(daysSum / daysCount).toFixed(1) : null,
  };
}

export default function OverallDashboard({ data, allowedBuyers: allowedBuyersProp, onSessionClick, onSendReminder, sendingReminder, reminderCooldowns, onFlowNavigate, flowCanOpen, showStyleProfit = false }) {
  // 2026-10-07 accounts: re-render when department owners load; allowedBuyers is derived from the viewer's departments in accounts mode
  const deptVer = useDepartmentMap();
  const allowedBuyers = useMemo(() => resolveAllowedBuyers(allowedBuyersProp), [allowedBuyersProp, deptVer]);
  const isAdmin = !allowedBuyers;
  const [sort, setSort] = useState(SORT_INIT);
  const [activeBuyer, setActiveBuyer] = useState(allowedBuyers ? allowedBuyers[0] : null);
  const [expanded, setExpanded] = useState(new Set());
  // 2026-10-07 accounts: once owners load, keep the selected buyer valid for this viewer.
  // Runs only when owners / access change (not on every buyer click), so the current pick is read through a ref.
  const activeBuyerRef = useRef(activeBuyer);
  activeBuyerRef.current = activeBuyer;
  useEffect(() => {
    if (!deptVer) return;
    if (allowedBuyers) { if (!allowedBuyers.includes(activeBuyerRef.current)) setActiveBuyer(allowedBuyers[0]); }
  }, [deptVer, allowedBuyers]);

  // Style quick-lookup
  const [lookupInput, setLookupInput] = useState("");
  const [lookupResult, setLookupResult] = useState(null); // { style, results } | null
  const [lookupLoading, setLookupLoading] = useState(false);
  const lookupRef = useRef(null);

  const handleLookup = useCallback(async (val) => {
    const q = (val || lookupInput).trim().toUpperCase();
    if (!q) { setLookupResult(null); return; }
    setLookupLoading(true);
    try {
      const d = await styleLookup(q);
      setLookupResult(d);
    } catch (e) {
      // 2026-10-13: a failed lookup is an error, not "not found" (was setLookupResult({ results: [] }))
      setLookupResult({ style: q, results: [], error: e?.response?.data?.detail || "Style lookup failed. Please try again." });
    } finally {
      setLookupLoading(false);
    }
  }, [lookupInput]);

  const toggleExpand = (id, e) => {
    e.stopPropagation();
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const { stats, sessions, po_file, dept_breakdown = [] } = data;

  // Buyer breakdown cards data (aggregated across all sessions)
  const buyerBreakdown = useMemo(() => {
    const map = {};
    dept_breakdown.forEach((d) => {
      const buyer = getBuyer(d.dept);
      if (!map[buyer]) map[buyer] = { total: 0, created: 0, daysSum: 0, daysCount: 0 };
      map[buyer].total += d.total;
      map[buyer].created += d.created;
      if (d.avg_days !== null) {
        map[buyer].daysSum += d.avg_days * d.created;
        map[buyer].daysCount += d.created;
      }
    });
    // 2026-10-07 accounts: owners present in the data, legacy order first (was a fixed BUYERS filter)
    return sortBuyerNames(Object.keys(map)).map((buyer) => {
      const v = map[buyer];
      return {
        buyer,
        total: v.total,
        created: v.created,
        pending: v.total - v.created,
        pct: v.total ? +(v.created / v.total * 100).toFixed(1) : 0,
        avg_days: v.daysCount ? +(v.daysSum / v.daysCount).toFixed(1) : null,
      };
    });
  }, [dept_breakdown, deptVer]);

  // Top stats: recompute for active buyer or use overall
  const displayStats = useMemo(() => {
    if (!activeBuyer) return stats;
    const b = buyerBreakdown.find((x) => x.buyer === activeBuyer);
    if (!b) return stats;
    return {
      total_sessions: stats.total_sessions,
      total_styles: b.total,
      total_po_created: b.created,
      total_pending: b.pending,
      po_cancelled: null,
      pct_created: b.pct,
      avg_days_to_po: b.avg_days,
    };
  }, [activeBuyer, stats, buyerBreakdown]);

  // Sessions table rows: recompute numbers for active buyer
  const displaySessions = useMemo(() => {
    if (!activeBuyer) return sessions.map((s) => ({ ...s, _display: null }));
    return sessions.map((s) => {
      const agg = aggregateForBuyer(s, activeBuyer);
      return { ...s, _display: agg };
    }).filter((s) => s._display.total > 0);
  }, [activeBuyer, sessions, deptVer]);

  const toggleSort = (col) =>
    setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }));
  const thClass = (col) => sort.col === col ? `sort-${sort.dir}` : "";

  const sorted = useMemo(() => [...displaySessions].sort((a, b) => {
    const getVal = (x) => x._display ? (x._display[sort.col] ?? x[sort.col]) : x[sort.col];
    let va = getVal(a), vb = getVal(b);
    if (va === null || va === undefined) va = sort.dir === "asc" ? "￿" : "";
    if (vb === null || vb === undefined) vb = sort.dir === "asc" ? "￿" : "";
    if (va < vb) return sort.dir === "asc" ? -1 : 1;
    if (va > vb) return sort.dir === "asc" ? 1 : -1;
    return 0;
  }), [displaySessions, sort]);

  const daysColor = (d) => {
    if (d === null) return "";
    if (d <= 7)  return "days-fast";
    if (d <= 14) return "days-medium";
    return "days-slow";
  };

  return (
    <>
      <FlowStrip sessions={sessions} onNavigate={onFlowNavigate} canOpen={flowCanOpen} />
      {showStyleProfit && (
        <div style={{ ...ui.card, marginBottom: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <h3 style={{ fontSize: 14, fontWeight: 650, color: "var(--text)", margin: 0 }}>Style profit</h3>
          <StyleProfitLookup />
        </div>
      )}

      {/* Top stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Sessions</div>
          <div className="stat-value primary">{displayStats.total_sessions}</div>
          <div className="stat-sub">reorder uploads</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Styles</div>
          <div className="stat-value primary">{displayStats.total_styles}</div>
          <div className="stat-sub">{activeBuyer ? `${activeBuyer} only` : "across all sessions"}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">PO Created</div>
          <div className="stat-value success">{displayStats.total_po_created}</div>
          <div className="stat-sub"><span className="pct green">{displayStats.pct_created}%</span> of total</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">PO Pending</div>
          <div className="stat-value danger">{displayStats.total_pending}</div>
          <div className="stat-sub"><span className="pct red">{displayStats.total_styles ? (displayStats.total_pending / displayStats.total_styles * 100).toFixed(1) : 0}%</span> of total</div>
        </div>
        {(displayStats.po_cancelled > 0) && (
          <div className="stat-card">
            <div className="stat-label">PO Cancelled</div>
            <div className="stat-value warning">{displayStats.po_cancelled}</div>
            <div className="stat-sub">fully cancelled, no GRN</div>
          </div>
        )}
        {(displayStats.buyer_rejected > 0) && (
          <div className="stat-card">
            <div className="stat-label">Buyer Rejected</div>
            <div className="stat-value warning">{displayStats.buyer_rejected}</div>
            <div className="stat-sub">excluded from pending</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-label">Avg Days to PO</div>
          <div className={`stat-value ${!displayStats.avg_days_to_po ? "primary" : displayStats.avg_days_to_po <= 7 ? "success" : displayStats.avg_days_to_po <= 14 ? "warning" : "danger"}`}>
            {displayStats.avg_days_to_po ?? "—"}
          </div>
          <div className="stat-sub">overall average</div>
        </div>
      </div>

      {/* Buyer breakdown cards — clickable */}
      {buyerBreakdown.length > 0 && (
        <>
          <div className="section-header" style={{ marginTop: 8 }}>
            <h3>Buyer-wise Summary</h3>
            {(isAdmin || (allowedBuyers && allowedBuyers.length > 1)) && (
              <div className="session-info-line">
                {activeBuyer
                  ? <><strong style={{ color: BUYER_COLORS[activeBuyer]?.text }}>{activeBuyer}</strong> selected{isAdmin ? " — click again to clear" : ""}</>
                  : "Click a buyer to filter"}
              </div>
            )}
          </div>
          <div className="buyer-breakdown-grid">
            {buyerBreakdown.filter((b) => isAdmin || allowedBuyers.includes(b.buyer)).map((b) => {
              const color = BUYER_COLORS[b.buyer] || NO_COLOR;
              const isActive = activeBuyer === b.buyer;
              const canSwitch = isAdmin || allowedBuyers.length > 1;
              return (
                <div
                  key={b.buyer}
                  className={`buyer-breakdown-card ${isActive ? "buyer-card-active" : ""}`}
                  style={{
                    borderTop: `3px solid ${color.text}`,
                    cursor: canSwitch ? "pointer" : "default",
                    opacity: canSwitch && activeBuyer && !isActive ? 0.5 : 1,
                    borderColor: isActive ? color.text : undefined,
                    boxShadow: isActive ? `0 0 0 1px ${color.text}, var(--shadow-sm)` : undefined,
                  }}
                  onClick={() => canSwitch && setActiveBuyer(isAdmin && isActive ? null : b.buyer)}
                >
                  <div className="buyer-breakdown-name" style={{ color: color.text, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {b.buyer}
                    {isActive && <span style={{ fontSize: 11, fontWeight: 600, background: color.bg, padding: "1px 7px", borderRadius: 999 }}>✓ Active</span>}
                  </div>
                  <div className="buyer-breakdown-row">
                    <span className="buyer-breakdown-label">Styles</span>
                    <span className="buyer-breakdown-val">{b.total}</span>
                  </div>
                  <div className="buyer-breakdown-row">
                    <span className="buyer-breakdown-label">PO Created</span>
                    <span className="buyer-breakdown-val" style={{ color: "var(--success)", fontWeight: 700 }}>{b.created}</span>
                  </div>
                  <div className="buyer-breakdown-row">
                    <span className="buyer-breakdown-label">PO Pending</span>
                    <span className="buyer-breakdown-val" style={{ color: b.pending > 0 ? "var(--danger)" : "var(--text-muted)", fontWeight: 700 }}>{b.pending}</span>
                  </div>
                  <div style={{ margin: "8px 0 4px" }}>
                    <PctBar pct={b.pct} />
                  </div>
                  <div className="buyer-breakdown-row">
                    <span className="buyer-breakdown-label">Avg Days</span>
                    <span className={`buyer-breakdown-val ${daysColor(b.avg_days)}`} style={{ fontWeight: 700 }}>
                      {b.avg_days !== null ? `${b.avg_days}d` : "—"}
                    </span>
                  </div>
                  {onSendReminder && b.pending > 0 && (() => {
                    const cd = reminderCooldowns?.[b.buyer];
                    const isLocked = cd?.locked === true;
                    const isSending = sendingReminder === b.buyer;
                    const anySending = !!sendingReminder;
                    return (
                      <button
                        style={{
                          marginTop: 10,
                          width: "100%",
                          padding: "6px 0",
                          borderRadius: "var(--radius-sm)",
                          border: isLocked
                            ? "1px solid var(--border)"
                            : `1px solid ${color.text}`,
                          background: isSending
                            ? color.bg
                            : isLocked
                            ? "var(--bg-hover)"
                            : "transparent",
                          color: isLocked ? "var(--text-muted)" : color.text,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: isLocked || anySending ? "not-allowed" : "pointer",
                          opacity: !isLocked && anySending && !isSending ? 0.45 : 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          transition: "background var(--t-fast)",
                        }}
                        disabled={isLocked || anySending}
                        onClick={(e) => { e.stopPropagation(); if (!isLocked) onSendReminder(b.buyer); }}
                        title={
                          isLocked
                            ? `Next reminder allowed in ${cd.hours_remaining}h`
                            : `Send PO reminder email to ${b.buyer}`
                        }
                      >
                        {isSending ? (
                          <><Spinner size={12} /> Sending…</>
                        ) : isLocked ? (
                          <>Locked — {cd.hours_remaining}h</>
                        ) : (
                          <>Send reminder</>
                        )}
                      </button>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Style quick-lookup */}
      <div style={{ ...ui.card, margin: "20px 0 0", padding: "12px 14px" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            ref={lookupRef}
            value={lookupInput}
            onChange={e => { setLookupInput(e.target.value); if (!e.target.value.trim()) setLookupResult(null); }}
            onKeyDown={e => e.key === "Enter" && handleLookup()}
            onPaste={e => {
              e.preventDefault();
              const val = e.clipboardData.getData("text").trim();
              if (val) { setLookupInput(val); setTimeout(() => handleLookup(val), 0); }
            }}
            placeholder="Style lookup — paste or type a style number to see which sessions it was ordered in"
            aria-label="Style lookup"
            style={{ ...ui.input, flex: "1 1 260px", minWidth: 0, fontFamily: "var(--font-mono)" }}
          />
          <Button variant="primary"
            onClick={() => handleLookup()}
            disabled={lookupLoading || !lookupInput.trim()}
          >{lookupLoading ? <><Spinner size={12} /> Looking up…</> : "Look up"}</Button>
          {lookupResult && (
            <Button variant="ghost" onClick={() => { setLookupResult(null); setLookupInput(""); }} aria-label="Clear lookup">Clear</Button>
          )}
        </div>

        {lookupResult && (
          <div style={{ marginTop: 14 }}>
            {lookupResult.error ? (
              <ErrorState message={<><strong>{lookupResult.style}</strong> — {lookupResult.error}</>} onRetry={() => handleLookup(lookupResult.style)} />
            ) : lookupResult.results.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "8px 0" }}>
                <strong>{lookupResult.style}</strong> — {lookupResult.archived_sessions ? `archived (not ordered) in ${lookupResult.archived_sessions} session${lookupResult.archived_sessions > 1 ? "s" : ""}, no other sessions.` : "not found in any session."}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 650, color: "var(--text)", marginBottom: 8 }}>
                  <span style={{ fontFamily: "var(--font-mono)" }}>{lookupResult.style}</span> <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>— found in {lookupResult.results.length} session{lookupResult.results.length > 1 ? "s" : ""}{lookupResult.archived_sessions ? ` · ${lookupResult.archived_sessions} archived session${lookupResult.archived_sessions > 1 ? "s" : ""} hidden` : ""}</span>
                </div>
                <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={lkTh}>Session Date</th>
                        <th style={lkTh}>Sizes</th>
                        <th style={{ ...lkTh, textAlign: "right" }}>Req Qty</th>
                        <th style={lkTh} title="What the buyer / CP approval decided for this style in that session">Decision</th>
                        <th style={lkTh}>PO Status</th>
                        <th style={{ ...lkTh, textAlign: "right" }}>PO Qty</th>
                        <th style={{ ...lkTh, textAlign: "right" }}>Excess</th>
                        <th style={{ ...lkTh, textAlign: "right" }}>GRN</th>
                        <th style={lkTh}>PO Numbers</th>
                        <th style={lkTh}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lookupResult.results.map((r, i) => (
                        <tr key={r.session_id} style={{ borderBottom: i < lookupResult.results.length - 1 ? "1px solid var(--border)" : "none" }}>
                          <td style={lkTd}><strong>{fmtDate(r.request_date)}</strong></td>
                          <td style={{ ...lkTd, color: "var(--text-muted)", fontSize: 12 }}>{r.sizes || "—"}</td>
                          <td style={{ ...lkTd, ...ui.num, fontWeight: 600 }}>{(r.total_requested_qty || 0).toLocaleString()}</td>
                          <td style={lkTd}>
                            {(() => {
                              const by = r.decision_by ? ` by ${r.decision_by}` : "";
                              const at = r.decision_at ? ` on ${fmtDate(r.decision_at)}` : "";
                              switch (r.decision) {
                                case "archived": return <Badge tone="neutral" title={`Not ordered this cycle${by}${at}`}>Archived (not ordered)</Badge>;
                                case "rejected": return <Badge tone="danger" title={`${r.decision_reason || "Rejected"}${r.decision_note ? ` - ${r.decision_note}` : ""}${by}${at}`}>Rejected{r.decision_reason ? ` · ${String(r.decision_reason).replace(/_/g, " ")}` : ""}</Badge>;
                                case "cp_rejected": return <Badge tone="danger" title="Rejected in CP Value Approvals">CP rejected</Badge>;
                                case "cp_pending": return <Badge tone="warning" title="Waiting in CP Value Approvals">CP pending</Badge>;
                                case "qty_edited": return <Badge tone="accent">Qty edited</Badge>;
                                case "accepted": return <Badge tone="success">Accepted</Badge>;
                                default: return <span style={{ color: "var(--text-muted)" }}>—</span>;
                              }
                            })()}
                          </td>
                          <td style={lkTd}>
                            {r.po_cancelled
                              ? <Badge tone="neutral">✕ PO Cancelled</Badge>
                              : r.po_created
                                ? <Badge tone="success">✓ PO Created</Badge>
                                : r.non_ecom_po
                                  ? <Badge tone="warning">↗ Non-Ecom</Badge>
                                  : r.archived
                                    ? <Badge tone="neutral" title="Session archived — no PO was created before it was archived">Session archived</Badge>
                                    : ["archived", "rejected", "cp_rejected"].includes(r.decision)
                                      ? <span style={{ color: "var(--text-muted)" }} title="No PO expected - see Decision">— not ordered</span>
                                      : <Badge tone="danger">✗ Pending</Badge>}
                          </td>
                          <td style={{ ...lkTd, ...ui.num, fontWeight: 600 }}>
                            {r.total_po_qty > 0 ? r.total_po_qty.toLocaleString() : <span style={{ color: "var(--text-muted)" }}>—</span>}
                          </td>
                          <td style={{ ...lkTd, ...ui.num }}>
                            {r.excess_qty > 0
                              ? <span style={{ color: "var(--warning)", fontWeight: 700 }}>+{r.excess_qty.toLocaleString()}</span>
                              : <span style={{ color: "var(--text-muted)" }}>—</span>}
                          </td>
                          <td style={{ ...lkTd, ...ui.num }}>
                            {r.grn_qty > 0 ? r.grn_qty.toLocaleString() : <span style={{ color: "var(--text-muted)" }}>—</span>}
                          </td>
                          <td style={{ ...lkTd, fontSize: 12, color: "var(--text-muted)", maxWidth: 260, overflowWrap: "anywhere" }}>
                            {r.po_numbers?.length > 0 ? r.po_numbers.join(", ") : "—"}
                            {r.po_window_end && (
                              <span style={{ marginLeft: 6, color: "var(--text-muted)", fontSize: 10 }} title={`POs counted up to ${fmtDate(r.po_window_end)}`}>
                                window→{fmtDate(r.po_window_end)}
                              </span>
                            )}
                          </td>
                          <td style={lkTd}>
                            <Button size="sm" variant="secondary" onClick={() => onSessionClick(r.session_id)}>View →</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Sessions table */}
      <div className="section-header" style={{ marginTop: 24 }}>
        <div>
          <h3>
            {activeBuyer ? `${activeBuyer} — Sessions Breakdown` : "All Sessions Breakdown"}
          </h3>
          <div className="session-info-line" title="Click a row to open that session">
            PO source: <strong>{po_file}</strong>
          </div>
        </div>
        {activeBuyer && (
          <Button size="sm" variant="secondary" onClick={() => setActiveBuyer(null)}>
            ✕ Clear filter
          </Button>
        )}
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 32 }}></th>
              <th className={thClass("request_date")} onClick={() => toggleSort("request_date")}>Request Date</th>
              <th className={thClass("filename")} onClick={() => toggleSort("filename")}>File</th>
              <th className={thClass("upload_date")} onClick={() => toggleSort("upload_date")}>Uploaded</th>
              <th className={thClass("total_styles")} onClick={() => toggleSort("total_styles")} style={{ textAlign: "right" }}>Styles</th>
              <th className={thClass("po_created")} onClick={() => toggleSort("po_created")} style={{ textAlign: "right" }}>PO Created</th>
              <th className={thClass("po_pending")} onClick={() => toggleSort("po_pending")} style={{ textAlign: "right" }}>PO Pending</th>
              <th className={thClass("pct_created")} onClick={() => toggleSort("pct_created")} style={{ textAlign: "right" }}>PO %</th>
              <th className={thClass("avg_days_to_po")} onClick={() => toggleSort("avg_days_to_po")} style={{ textAlign: "center" }}>Avg Days</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => {
              const d = s._display;
              const total    = d ? d.total    : s.total_styles;
              const created  = d ? d.created  : s.po_created;
              const pending  = d ? d.pending  : s.po_pending;
              const pct      = d ? d.pct      : s.pct_created;
              const avgDays  = d ? d.avg_days : s.avg_days_to_po;
              const isOpen   = expanded.has(s._id);
              const depts    = Object.entries(s.dept_breakdown || {});
              return (
                <React.Fragment key={s._id}>
                  <tr style={{ cursor: "pointer" }} onClick={() => onSessionClick(s._id)}>
                    <td onClick={(e) => toggleExpand(s._id, e)} style={{ textAlign: "center", cursor: "pointer", color: "var(--text-muted)", fontSize: 13, userSelect: "none" }}>
                      {depts.length > 0 ? (isOpen ? "▾" : "▸") : ""}
                    </td>
                    <td style={{ fontWeight: 600 }}>{fmtDate(s.request_date)}</td>
                    <td style={{ fontSize: 12, color: "var(--text-muted)", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.filename}>{s.filename}</td>
                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{fmtDate(s.upload_date)}</td>
                    <td style={{ ...ui.num, fontWeight: 600 }}>{total}</td>
                    <td style={{ textAlign: "right" }}><span className="badge green">{created}</span></td>
                    <td style={{ textAlign: "right" }}>
                      {pending > 0 ? <span className="badge red">{pending}</span> : <span style={{ color: "var(--text-muted)" }}>—</span>}
                    </td>
                    <td style={{ textAlign: "right" }}><PctBar pct={pct} /></td>
                    <td className={`days-cell ${daysColor(avgDays)}`}>
                      {avgDays !== null ? `${avgDays}d` : "—"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Button size="sm" variant="secondary"
                        onClick={(e) => { e.stopPropagation(); onSessionClick(s._id); }}>
                        View →
                      </Button>
                    </td>
                  </tr>
                  {isOpen && depts.length > 0 && (
                    <tr>
                      <td colSpan={10} style={{ padding: 0, background: "var(--bg-card)" }}>
                        <DeptBreakdown depts={depts} activeBuyer={activeBuyer} daysColor={daysColor} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function sortRows(rows, col, dir) {
  return [...rows].sort((a, b) => {
    let va = a[col] ?? (dir === "asc" ? Infinity : -Infinity);
    let vb = b[col] ?? (dir === "asc" ? Infinity : -Infinity);
    if (typeof va === "string") va = va.toLowerCase();
    if (typeof vb === "string") vb = vb.toLowerCase();
    if (va < vb) return dir === "asc" ? -1 : 1;
    if (va > vb) return dir === "asc" ? 1 : -1;
    return 0;
  });
}

function DeptBreakdown({ depts, activeBuyer, daysColor }) {
  const deptVer = useDepartmentMap(); // 2026-10-07 accounts
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [groupBy, setGroupBy] = useState("dept");
  const [sort, setSort] = useState({ col: "total", dir: "desc" });
  const [subSort, setSubSort] = useState({ col: "created", dir: "desc" });

  const toggleKey = useCallback((key, e) => {
    e.stopPropagation();
    setExpandedKeys((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }, []);

  const toggleSort = (col) => setSort((s) => ({ col, dir: s.col === col && s.dir === "desc" ? "asc" : "desc" }));
  const toggleSub  = (col) => setSubSort((s) => ({ col, dir: s.col === col && s.dir === "desc" ? "asc" : "desc" }));
  const arrow = (col, s) => s.col === col ? (s.dir === "asc" ? " ▲" : " ▼") : "";

  const filtered = useMemo(() =>
    depts.filter(([dept]) => !activeBuyer || getBuyer(dept) === activeBuyer),
  [depts, activeBuyer, deptVer]);

  // Build vendor-first rows by aggregating across depts
  const vendorRows = useMemo(() => {
    const map = {};
    filtered.forEach(([dept, v]) => {
      Object.entries(v.vendors || {}).forEach(([vendor, vv]) => {
        if (!map[vendor]) map[vendor] = { name: vendor, total: 0, created: 0, pending: 0, daysSum: 0, daysCount: 0, depts: {} };
        map[vendor].total   += vv.total;
        map[vendor].created += vv.created;
        map[vendor].pending += vv.pending;
        if (vv.avg_days !== null) { map[vendor].daysSum += vv.avg_days * vv.created; map[vendor].daysCount += vv.created; }
        map[vendor].depts[dept] = { ...vv, buyer: getBuyer(dept) };
      });
    });
    return Object.values(map).map((v) => ({
      ...v,
      avg_days: v.daysCount ? +(v.daysSum / v.daysCount).toFixed(1) : null,
      pct: v.total ? +(v.created / v.total * 100).toFixed(1) : 0,
    }));
  }, [filtered, deptVer]);

  // Build dept rows
  const deptRows = useMemo(() =>
    filtered.map(([dept, v]) => ({
      name: dept, buyer: getBuyer(dept), ...v,
      pct: v.total ? +(v.created / v.total * 100).toFixed(1) : 0,
    })),
  [filtered, deptVer]);

  const outerRows = useMemo(() => sortRows(groupBy === "dept" ? deptRows : vendorRows, sort.col, sort.dir), [groupBy, deptRows, vendorRows, sort]);
  if (outerRows.length === 0) return null;

  const thStyle = (col) => ({
    padding: "6px 8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
    fontSize: 11, color: sort.col === col ? "var(--text)" : "var(--text-muted)",
    cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
  });
  const subThStyle = (col) => ({
    ...thStyle(col),
    color: subSort.col === col ? "var(--text)" : "var(--text-muted)",
  });

  return (
    <div style={{ padding: "10px 16px 14px 40px", overflowX: "auto" }}>
      {/* Toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Group by</span>
        <Segmented ariaLabel="Group by" value={groupBy}
          options={[{ value: "dept", label: "Department" }, { value: "vendor", label: "Vendor" }]}
          onChange={(g) => { setGroupBy(g); setExpandedKeys(new Set()); }} />
      </div>

      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--border)" }}>
            <th style={{ width: 28 }}></th>
            <th style={{ ...thStyle("name"), textAlign: "left" }} onClick={() => toggleSort("name")}>
              {groupBy === "dept" ? "Department" : "Vendor"}{arrow("name", sort)}
            </th>
            {groupBy === "dept" && <th style={{ ...thStyle("buyer"), textAlign: "left" }} onClick={() => toggleSort("buyer")}>Buyer{arrow("buyer", sort)}</th>}
            <th style={{ ...thStyle("total"), textAlign: "right" }} onClick={() => toggleSort("total")}>Styles{arrow("total", sort)}</th>
            <th style={{ ...thStyle("req_qty"), textAlign: "right" }} onClick={() => toggleSort("req_qty")}>Req Qty{arrow("req_qty", sort)}</th>
            <th style={{ ...thStyle("po_qty"), textAlign: "right" }} onClick={() => toggleSort("po_qty")}>PO Qty{arrow("po_qty", sort)}</th>
            <th style={{ ...thStyle("pending_qty"), textAlign: "right" }} onClick={() => toggleSort("pending_qty")}>Pending Qty{arrow("pending_qty", sort)}</th>
            <th style={{ ...thStyle("created"), textAlign: "right" }} onClick={() => toggleSort("created")}>Booked{arrow("created", sort)}</th>
            <th style={{ ...thStyle("pending"), textAlign: "right" }} onClick={() => toggleSort("pending")}>Pending{arrow("pending", sort)}</th>
            <th style={{ ...thStyle("pct"), textAlign: "right" }} onClick={() => toggleSort("pct")}>PO %{arrow("pct", sort)}</th>
            <th style={{ ...thStyle("avg_days"), textAlign: "center" }} onClick={() => toggleSort("avg_days")}>Avg Days{arrow("avg_days", sort)}</th>
          </tr>
        </thead>
        <tbody>
          {outerRows.map((row) => {
            const isOpen = expandedKeys.has(row.name);
            const subEntries = groupBy === "dept"
              ? sortRows(Object.entries(row.vendors || {}).map(([v, vv]) => ({ name: v, ...vv, pct: vv.total ? +(vv.created / vv.total * 100).toFixed(1) : 0 })), subSort.col, subSort.dir)
              : sortRows(Object.entries(row.depts || {}).map(([d, dv]) => ({ name: d, buyer: dv.buyer || getBuyer(d), ...dv, pct: dv.total ? +(dv.created / dv.total * 100).toFixed(1) : 0 })), subSort.col, subSort.dir);
            const hasChildren = subEntries.length > 0;

            return (
              <React.Fragment key={row.name}>
                <tr style={{ borderBottom: isOpen ? "none" : "1px solid var(--border)" }}>
                  <td style={{ textAlign: "center", cursor: hasChildren ? "pointer" : "default", color: "var(--text-muted)", userSelect: "none", fontSize: 12 }}
                    onClick={(e) => hasChildren && toggleKey(row.name, e)}>
                    {hasChildren ? (isOpen ? "▾" : "▸") : ""}
                  </td>
                  <td style={{ padding: "6px 8px", fontWeight: 600, color: groupBy === "vendor" && row.name === "Mapping required" ? "var(--warning)" : "var(--text)", fontStyle: row.name === "Mapping required" ? "italic" : "normal" }}>{row.name}</td>
                  {groupBy === "dept" && (
                    <td style={{ padding: "6px 8px" }}>
                      {(() => { const c = BUYER_COLORS[row.buyer] || NO_COLOR; return <span style={{ background: c.bg, color: c.text, padding: "1px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 600 }}>{row.buyer}</span>; })()}
                    </td>
                  )}
                  <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600 }}>{row.total}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, color: "var(--accent)" }}>{row.req_qty > 0 ? row.req_qty.toLocaleString() : "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, color: row.po_qty > 0 ? "var(--success)" : "var(--text-muted)" }}>{row.po_qty > 0 ? row.po_qty.toLocaleString() : "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: row.pending_qty > 0 ? "var(--danger)" : "var(--text-muted)" }}>{row.pending_qty > 0 ? row.pending_qty.toLocaleString() : "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", color: row.created > 0 ? "var(--success)" : "var(--text-muted)", fontWeight: 700 }}>{row.created > 0 ? row.created : "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", color: row.pending > 0 ? "var(--danger)" : "var(--text-muted)", fontWeight: 700 }}>{row.pending > 0 ? row.pending : "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}><PctBar pct={row.pct} /></td>
                  <td style={{ padding: "6px 8px", textAlign: "center" }} className={`days-cell ${daysColor(row.avg_days)}`}>{row.avg_days !== null ? `${row.avg_days}d` : "—"}</td>
                </tr>

                {/* Sub-header when expanded */}
                {isOpen && (
                  <tr style={{ background: "var(--bg-hover)" }}>
                    <td></td>
                    <td style={{ ...subThStyle("name"), padding: "4px 8px 4px 20px", textAlign: "left" }} onClick={() => toggleSub("name")}>
                      {groupBy === "dept" ? "Vendor" : "Department"}{arrow("name", subSort)}
                    </td>
                    {groupBy === "vendor" && <td style={{ ...subThStyle("buyer"), padding: "4px 8px", textAlign: "left" }} onClick={() => toggleSub("buyer")}>Buyer{arrow("buyer", subSort)}</td>}
                    <td style={{ ...subThStyle("total"), padding: "4px 8px", textAlign: "right" }} onClick={() => toggleSub("total")}>Styles{arrow("total", subSort)}</td>
                    <td style={{ ...subThStyle("req_qty"), padding: "4px 8px", textAlign: "right" }} onClick={() => toggleSub("req_qty")}>Req Qty{arrow("req_qty", subSort)}</td>
                    <td style={{ ...subThStyle("po_qty"), padding: "4px 8px", textAlign: "right" }} onClick={() => toggleSub("po_qty")}>PO Qty{arrow("po_qty", subSort)}</td>
                    <td style={{ ...subThStyle("pending_qty"), padding: "4px 8px", textAlign: "right" }} onClick={() => toggleSub("pending_qty")}>Pending Qty{arrow("pending_qty", subSort)}</td>
                    <td style={{ ...subThStyle("created"), padding: "4px 8px", textAlign: "right" }} onClick={() => toggleSub("created")}>Booked{arrow("created", subSort)}</td>
                    <td style={{ ...subThStyle("pending"), padding: "4px 8px", textAlign: "right" }} onClick={() => toggleSub("pending")}>Pending{arrow("pending", subSort)}</td>
                    <td style={{ ...subThStyle("pct"), padding: "4px 8px", textAlign: "right" }} onClick={() => toggleSub("pct")}>PO %{arrow("pct", subSort)}</td>
                    <td style={{ ...subThStyle("avg_days"), padding: "4px 8px", textAlign: "center" }} onClick={() => toggleSub("avg_days")}>Avg Days{arrow("avg_days", subSort)}</td>
                  </tr>
                )}

                {isOpen && subEntries.map((sub) => {
                  const isMR = sub.name === "Mapping required";
                  return (
                    <tr key={sub.name} style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-hover)" }}>
                      <td></td>
                      <td style={{ padding: "5px 8px 5px 20px", fontSize: 11, color: isMR ? "var(--warning)" : "var(--text)", fontStyle: isMR ? "italic" : "normal", fontWeight: isMR ? 600 : 400 }}>
                        ↳ {sub.name}
                      </td>
                      {groupBy === "vendor" && (
                        <td style={{ padding: "5px 8px" }}>
                          {(() => { const c = BUYER_COLORS[sub.buyer] || NO_COLOR; return <span style={{ background: c.bg, color: c.text, padding: "1px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 600 }}>{sub.buyer}</span>; })()}
                        </td>
                      )}
                      <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 600, fontSize: 12 }}>{sub.total || "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 600, fontSize: 12, color: "var(--accent)" }}>{sub.req_qty > 0 ? sub.req_qty.toLocaleString() : "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 600, fontSize: 12, color: sub.po_qty > 0 ? "var(--success)" : "var(--text-muted)" }}>{sub.po_qty > 0 ? sub.po_qty.toLocaleString() : "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, fontSize: 12, color: sub.pending_qty > 0 ? "var(--danger)" : "var(--text-muted)" }}>{sub.pending_qty > 0 ? sub.pending_qty.toLocaleString() : "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", color: sub.created > 0 ? "var(--success)" : "var(--text-muted)", fontWeight: 700, fontSize: 12 }}>{sub.created > 0 ? sub.created : "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", color: sub.pending > 0 ? "var(--danger)" : "var(--text-muted)", fontWeight: 700, fontSize: 12 }}>{sub.pending > 0 ? sub.pending : "—"}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right" }}><PctBar pct={sub.pct || 0} /></td>
                      <td style={{ padding: "5px 8px", textAlign: "center", fontSize: 12 }} className={`days-cell ${daysColor(sub.avg_days)}`}>{sub.avg_days !== null ? `${sub.avg_days}d` : "—"}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const lkTh = { ...ui.th, position: "static", padding: "7px 10px" };
const lkTd = { padding: "7px 10px", fontSize: 13, verticalAlign: "middle", color: "var(--text)" };

function PctBar({ pct }) {
  const color = pct >= 80 ? "var(--success)" : pct >= 50 ? "var(--warning)" : "var(--danger)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
      <div style={{ width: 60, height: 6, background: "var(--border)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color, minWidth: 40, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
    </div>
  );
}
